import { useState, useEffect } from "react";
import {
  Card,
  Col,
  Row,
  Typography,
  Progress,
  Button,
  Timeline,
  Spin,
  Alert,
  Table,
  Tag,
  Tabs,
  Space,
  Tooltip,
} from "antd";
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  StockOutlined,
  GiftOutlined,
  LoadingOutlined,
  PieChartOutlined,
  BankOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";

import Echart from "../components/chart/EChart";
import LineChart from "../components/chart/LineChart";
import AccountHoldingsCard from "../components/AccountHoldingsCard";
import PortfolioSummaryCard from "../components/PortfolioSummaryCard";
import HoldingsHeatmap from "../components/HoldingsHeatmap";
import { 
  getDashboardStats, 
  getMonthlyDividendStats,
  getEnhancedDashboardStats,
  getDailyHoldingsByAccount,
  getCurrentStockPrice,
  formatCurrency,
  formatPercentage,
  calculateProfitLossRate,
  refreshDashboardData
} from "../services/dashboardService";

function Home() {
  const { Title, Text } = Typography;
  
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlyDividendData, setMonthlyDividendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [holdingsWithPrices, setHoldingsWithPrices] = useState([]);
  const [rawHoldings, setRawHoldings] = useState([]); // 원본 데이터 (총액 계산용)
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // 자산 가리기 상태 (localStorage에서 Account Balance와 동일한 키 사용)
  const [hideAmounts, setHideAmounts] = useState(() => {
    const saved = localStorage.getItem('hideAmounts');
    return saved === 'true';
  });
  
  // 자산 가리기 토글 핸들러
  const toggleHideAmounts = () => {
    setHideAmounts(prev => {
      const newValue = !prev;
      localStorage.setItem('hideAmounts', String(newValue));
      return newValue;
    });
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard stats, monthly dividend data, and daily holdings in parallel
      const [statsData, dividendData, dailyHoldingsData] = await Promise.all([
        getDashboardStats(),
        getMonthlyDividendStats(),
        getDailyHoldingsByAccount() // 새로운 API 사용
      ]);

      setDashboardData(statsData);
      setMonthlyDividendData(dividendData);

      // 새로운 DailyHolding API 데이터로 보유 주식 현황 설정
      if (dailyHoldingsData && dailyHoldingsData.results && dailyHoldingsData.results.length > 0) {
        // 원본 데이터 저장 (총액 계산용)
        const rawData = dailyHoldingsData.results.map(holding => ({
          id: holding.id,
          stock_symbol: holding.stock_symbol,
          stock_name: holding.stock_name,
          quantity: holding.quantity,
          avg_buy_price: parseFloat(holding.avg_buy_price),
          market_value: holding.market_value || 0,
          profit_loss_amount: holding.unrealized_pnl || 0,
          account_name: holding.account_name
        }));
        setRawHoldings(rawData);
        
        // 종목별로 통합
        const holdingsMap = new Map();
        
        dailyHoldingsData.results.forEach(holding => {
          const symbol = holding.stock_symbol;
          
          if (holdingsMap.has(symbol)) {
            const existing = holdingsMap.get(symbol);
            const totalQuantity = existing.quantity + holding.quantity;
            const totalCost = (existing.avg_buy_price * existing.quantity) + 
                            (parseFloat(holding.avg_buy_price) * holding.quantity);
            const newAvgPrice = totalCost / totalQuantity;
            
            holdingsMap.set(symbol, {
              ...existing,
              quantity: totalQuantity,
              avg_buy_price: newAvgPrice,
              market_value: existing.market_value + (parseFloat(holding.market_value) || 0),
              profit_loss_amount: existing.profit_loss_amount + (parseFloat(holding.unrealized_pnl) || 0),
              account_name: `${existing.account_name}, ${holding.account_name}` // 계좌명 합치기
            });
          } else {
            holdingsMap.set(symbol, {
              id: holding.id,
              stock_symbol: holding.stock_symbol,
              stock_name: holding.stock_name,
              quantity: holding.quantity,
              avg_buy_price: parseFloat(holding.avg_buy_price),
              market_value: parseFloat(holding.market_value) || 0,
              profit_loss_amount: parseFloat(holding.unrealized_pnl) || 0,
              account_name: holding.account_name
            });
          }
        });
        
        // Map을 배열로 변환하고 현재가와 수익률 계산
        const holdingsWithPrices = Array.from(holdingsMap.values()).map(holding => {
          const marketValue = parseFloat(holding.market_value) || 0;
          const quantity = parseFloat(holding.quantity) || 0;
          const avgBuyPrice = parseFloat(holding.avg_buy_price) || 0;
          const profitLossAmount = parseFloat(holding.profit_loss_amount) || 0;
          
          // 현재가 계산: 평균매수가 + (손익금액 / 수량)
          // 이 방식이 더 정확함 - 종가 시점 차이의 영향을 받지 않음
          const currentPrice = quantity > 0 
            ? avgBuyPrice + (profitLossAmount / quantity)
            : 0;
          
          // 수익률 계산
          const profitLossRate = avgBuyPrice > 0 
            ? ((currentPrice - avgBuyPrice) / avgBuyPrice) * 100
            : 0;
          
          // console.log(`통합 종목: ${holding.stock_name}, 수량: ${quantity}주, 평균단가: ${avgBuyPrice.toLocaleString()}원, 현재가: ${currentPrice.toLocaleString()}원, 평가금액: ${marketValue.toLocaleString()}원, 수익률: ${profitLossRate.toFixed(2)}%`);
          
          return {
            ...holding,
            current_price: currentPrice,
            profit_loss_rate: profitLossRate,
            market_value: marketValue // 명시적으로 설정
          };
        });
        
        setHoldingsWithPrices(holdingsWithPrices);
      } else {
        setRawHoldings([]);
        setHoldingsWithPrices([]);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshData = async () => {
    try {
      setRefreshing(true);
      setError(null);

      // console.log('데이터 refresh 시작...');
      
      // Backend DB 업데이트 + Frontend 데이터 재조회
      const refreshResult = await refreshDashboardData();
      
      // console.log('Refresh 완료:', refreshResult);
      
      // 상태 업데이트
      setDashboardData(refreshResult.dashboardStats);
      setMonthlyDividendData(refreshResult.monthlyDividendStats);

      // 새로운 DailyHolding API 데이터로 보유 주식 현황 설정
      if (refreshResult.dailyHoldingsData && refreshResult.dailyHoldingsData.results && refreshResult.dailyHoldingsData.results.length > 0) {
        // 원본 데이터 저장 (총액 계산용)
        const rawData = refreshResult.dailyHoldingsData.results.map(holding => ({
          id: holding.id,
          stock_symbol: holding.stock_symbol,
          stock_name: holding.stock_name,
          quantity: holding.quantity,
          avg_buy_price: parseFloat(holding.avg_buy_price),
          market_value: holding.market_value || 0,
          profit_loss_amount: holding.unrealized_pnl || 0,
          account_name: holding.account_name
        }));
        setRawHoldings(rawData);
        
        // 종목별로 통합
        const holdingsMap = new Map();
        
        refreshResult.dailyHoldingsData.results.forEach(holding => {
          const symbol = holding.stock_symbol;
          
          if (holdingsMap.has(symbol)) {
            const existing = holdingsMap.get(symbol);
            const totalQuantity = existing.quantity + holding.quantity;
            const totalCost = (existing.avg_buy_price * existing.quantity) + 
                            (parseFloat(holding.avg_buy_price) * holding.quantity);
            const newAvgPrice = totalCost / totalQuantity;
            
            holdingsMap.set(symbol, {
              ...existing,
              quantity: totalQuantity,
              avg_buy_price: newAvgPrice,
              market_value: existing.market_value + (parseFloat(holding.market_value) || 0),
              profit_loss_amount: existing.profit_loss_amount + (parseFloat(holding.unrealized_pnl) || 0),
              account_name: `${existing.account_name}, ${holding.account_name}` // 계좌명 합치기
            });
          } else {
            holdingsMap.set(symbol, {
              id: holding.id,
              stock_symbol: holding.stock_symbol,
              stock_name: holding.stock_name,
              quantity: holding.quantity,
              avg_buy_price: parseFloat(holding.avg_buy_price),
              market_value: parseFloat(holding.market_value) || 0,
              profit_loss_amount: parseFloat(holding.unrealized_pnl) || 0,
              account_name: holding.account_name
            });
          }
        });
        
        // Map을 배열로 변환하고 현재가와 수익률 계산
        const holdingsWithPrices = Array.from(holdingsMap.values()).map(holding => {
          const marketValue = parseFloat(holding.market_value) || 0;
          const quantity = parseFloat(holding.quantity) || 0;
          const avgBuyPrice = parseFloat(holding.avg_buy_price) || 0;
          const profitLossAmount = parseFloat(holding.profit_loss_amount) || 0;
          
          // 현재가 계산: 평균매수가 + (손익금액 / 수량)
          // 이 방식이 더 정확함 - 종가 시점 차이의 영향을 받지 않음
          const currentPrice = quantity > 0 
            ? avgBuyPrice + (profitLossAmount / quantity)
            : 0;
          
          // 수익률 계산
          const profitLossRate = avgBuyPrice > 0 
            ? ((currentPrice - avgBuyPrice) / avgBuyPrice) * 100
            : 0;
          
          // console.log(`Refresh 통합 종목: ${holding.stock_name}, 수량: ${quantity}주, 평균단가: ${avgBuyPrice.toLocaleString()}원, 현재가: ${currentPrice.toLocaleString()}원, 평가금액: ${marketValue.toLocaleString()}원, 수익률: ${profitLossRate.toFixed(2)}%`);
          
          return {
            ...holding,
            current_price: currentPrice,
            profit_loss_rate: profitLossRate,
            market_value: marketValue // 명시적으로 설정
          };
        });
        
        setHoldingsWithPrices(holdingsWithPrices);
      } else {
        setRawHoldings([]);
        setHoldingsWithPrices([]);
      }

      // PortfolioSummaryCard refresh trigger 업데이트
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
      setError('데이터 refresh 중 오류가 발생했습니다.');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="layout-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="layout-content">
        <Alert
          message="오류"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchDashboardData}>
              다시 시도
            </Button>
          }
        />
      </div>
    );
  }

  // DailyHolding 데이터로부터 핵심 지표 계산
  const calculateKeyMetrics = () => {
    // 원본 데이터로 총액 계산 (중복 제거 전 데이터)
    if (!rawHoldings || rawHoldings.length === 0) {
      return {
        totalAssets: 0,
        stockValue: 0,
        todayProfitLoss: 0,
        holdingStocksCount: 0
      };
    }

    // console.log('Raw holdings data for key metrics:', rawHoldings); // 디버깅용

    // 주식 평가액 합계 (원본 데이터로 계산 - 모든 계좌의 평가액 합산)
    const stockValue = rawHoldings.reduce((sum, holding) => {
      const marketValue = parseFloat(holding.market_value) || 0;
      // console.log(`Market value for ${holding.stock_name} (${holding.account_name}):`, marketValue); // 디버깅용
      return sum + marketValue;
    }, 0);
    
    const todayProfitLoss = rawHoldings.reduce((sum, holding) => {
      const profitLoss = parseFloat(holding.profit_loss_amount) || 0;
      // console.log(`Profit/Loss for ${holding.stock_name} (${holding.account_name}):`, profitLoss); // 디버깅용
      return sum + profitLoss;
    }, 0);
    
    // 보유 종목 수는 통합된 데이터 기준
    const holdingStocksCount = holdingsWithPrices.length;

    // 총 자산 = 주식 평가액 (현금 잔고는 PortfolioSummaryCard에서 처리)
    // Note: 실제로는 Backend API에서 total_assets 정보를 받아와야 함
    const totalAssets = stockValue;

    // console.log('Key metrics calculation:', { totalAssets, stockValue, todayProfitLoss, holdingStocksCount }); // 디버깅용

    return {
      totalAssets: isNaN(totalAssets) ? 0 : totalAssets,
      stockValue: isNaN(stockValue) ? 0 : stockValue,
      todayProfitLoss: isNaN(todayProfitLoss) ? 0 : todayProfitLoss,
      holdingStocksCount: isNaN(holdingStocksCount) ? 0 : holdingStocksCount
    };
  };

  const calculatedMetrics = calculateKeyMetrics();

  // 핵심 지표 카드 데이터
  const keyMetrics = [
    {
      title: "총 주식 평가액",
      value: hideAmounts ? "******" : formatCurrency(calculatedMetrics.stockValue),
      change: "+0%", // TODO: 전일 대비 계산
      icon: <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />,
      color: "#1890ff",
      hideValue: hideAmounts,
    },
    {
      title: "총 평가 손익",
      value: hideAmounts ? "******" : formatCurrency(calculatedMetrics.todayProfitLoss),
      change: calculatedMetrics.todayProfitLoss >= 0 ? "+" : "",
      icon: calculatedMetrics.todayProfitLoss >= 0 ? 
        <RiseOutlined style={{ fontSize: '24px', color: '#fff' }} /> :
        <FallOutlined style={{ fontSize: '24px', color: '#fff' }} />,
      color: calculatedMetrics.todayProfitLoss >= 0 ? "#52c41a" : "#ff4d4f",
      hideValue: hideAmounts,
    },
    {
      title: "이번 달 배당금",
      value: hideAmounts ? "******" : formatCurrency(dashboardData?.monthly_dividends || 0),
      change: "+0%", // TODO: 전월 대비 계산
      icon: <GiftOutlined style={{ fontSize: '24px', color: '#fff' }} />,
      color: "#722ed1",
      hideValue: hideAmounts,
    },
    {
      title: "보유 종목 수",
      value: `${calculatedMetrics.holdingStocksCount}개`,
      change: "",
      icon: <StockOutlined style={{ fontSize: '24px', color: '#fff' }} />,
      color: "#fa8c16",
      hideValue: false, // 종목 수는 가리지 않음
    },
  ];

  // 보유 주식 테이블 컬럼 정의
  const holdingsColumns = [
    {
      title: '종목명',
      dataIndex: 'stock_name',
      key: 'stock_name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.stock_symbol}</div>
        </div>
      ),
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (value) => `${value.toLocaleString()}주`,
    },
    {
      title: '평균단가',
      dataIndex: 'avg_buy_price',
      key: 'avg_buy_price',
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: '현재가',
      dataIndex: 'current_price',
      key: 'current_price',
      align: 'right',
      render: (value) => value > 0 ? formatCurrency(value) : '-',
    },
    {
      title: '수익률',
      dataIndex: 'profit_loss_rate',
      key: 'profit_loss_rate',
      align: 'right',
      render: (value, record) => {
        if (record.current_price === 0) return '-';
        const color = value >= 0 ? '#52c41a' : '#ff4d4f';
        return (
          <Tag color={value >= 0 ? 'green' : 'red'}>
            {formatPercentage(value)}
          </Tag>
        );
      },
    },
  ];

  return (
    <>
      <div className="layout-content">
        {/* 데이터 기준 시점 표시 */}
        <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
          <Col xs={24}>
            <Card 
              bordered={false} 
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                marginBottom: 0
              }}
            >
              <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space align="center">
                  <ClockCircleOutlined style={{ fontSize: '18px', color: 'white' }} />
                  <div>
                    <Text style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                      데이터 기준 시점
                    </Text>
                    <br />
                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                      보유 현황: {dashboardData?.data_date || new Date().toLocaleDateString('ko-KR', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })} | 종가: {dashboardData?.price_date || '데이터 없음'}
                    </Text>
                  </div>
                </Space>
                <Space align="center">
                  <Tooltip title={hideAmounts ? "자산 금액 보기" : "자산 금액 가리기"}>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={hideAmounts ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      onClick={toggleHideAmounts}
                      style={{
                        borderColor: hideAmounts ? '#faad14' : 'rgba(255,255,255,0.8)',
                        color: 'white',
                        backgroundColor: hideAmounts ? 'rgba(250, 173, 20, 0.2)' : 'rgba(255,255,255,0.1)'
                      }}
                    >
                      {hideAmounts ? '보기' : '가리기'}
                    </Button>
                  </Tooltip>
                  <Tooltip title="데이터를 최신으로 업데이트합니다" placement="left">
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<ReloadOutlined spin={refreshing} />}
                      loading={refreshing}
                      onClick={handleRefreshData}
                      style={{
                        borderColor: 'rgba(255,255,255,0.8)',
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }}
                    >
                      {refreshing ? '업데이트 중...' : '새로고침'}
                    </Button>
                  </Tooltip>
                  <Tooltip 
                    title={`보유 현황은 ${dashboardData?.data_date || '데이터 없음'} 기준이며, 시가총액 계산에 사용된 종가는 ${dashboardData?.price_date || '데이터 없음'} 기준입니다. 실시간 데이터가 아님을 참고해 주세요.`}
                    placement="left"
                  >
                    <InfoCircleOutlined 
                      style={{ 
                        fontSize: '20px', 
                        color: 'rgba(255,255,255,0.8)',
                        cursor: 'pointer'
                      }} 
                    />
                  </Tooltip>
                </Space>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* 핵심 지표 카드 */}
        <Row className="rowgap-vbox" gutter={[24, 0]}>
          {keyMetrics.map((metric, index) => (
            <Col
              key={index}
              xs={24}
              sm={24}
              md={12}
              lg={6}
              xl={6}
              className="mb-24"
            >
              <Card bordered={false} className="criclebox">
                <div className="number">
                  <Row align="middle" gutter={[24, 0]}>
                    <Col xs={18}>
                      <span>{metric.title}</span>
                      <Title level={3}>
                        {metric.value}
                        {metric.change && (
                          <small 
                            className={metric.color === "#52c41a" ? "bnb2" : metric.color === "#ff4d4f" ? "redtext" : "bnb2"}
                          >
                            {metric.change}
                          </small>
                        )}
                      </Title>
                    </Col>
                    <Col xs={6}>
                      <div 
                        className="icon-box" 
                        style={{ backgroundColor: metric.color }}
                      >
                        {metric.icon}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* 차트 영역 */}
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={10} className="mb-24">
            <Card bordered={false} className="criclebox h-full">
              <Echart monthlyDividendData={monthlyDividendData} />
            </Card>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={14} className="mb-24">
            <Card bordered={false} className="criclebox h-full">
              <LineChart monthlyDividendData={monthlyDividendData} />
            </Card>
          </Col>
        </Row>

        {/* 새로운 포트폴리오 분석 섹션 */}
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12} className="mb-24">
            <PortfolioSummaryCard refreshTrigger={refreshTrigger} hideAmounts={hideAmounts} />
          </Col>
          <Col xs={24} lg={12} className="mb-24">
            <Card bordered={false} className="criclebox h-full">
              <div className="timeline-box">
                <Title level={5}>최근 배당금 내역</Title>
                <Text className="lastweek" style={{ marginBottom: 24 }}>
                  이번 달 배당금 {dashboardData?.recent_dividends?.length || 0}건
                </Text>

                {dashboardData?.recent_dividends?.length > 0 ? (
                  <Timeline className="timelinelist">
                    {dashboardData.recent_dividends.map((dividend, index) => (
                      <Timeline.Item key={dividend.id} color="green">
                        <div>
                          <Title level={5} style={{ margin: 0, fontSize: '14px' }}>
                            배당금 {dividend.stock_name}
                          </Title>
                          <Text style={{ fontSize: '12px', color: '#666' }}>
                            {hideAmounts ? '******' : formatCurrency(dividend.total_amount)}
                          </Text>
                          <br />
                          <Text style={{ fontSize: '12px', color: '#999' }}>
                            {dividend.date} | {dividend.account_name}
                          </Text>
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    <Text>이번 달 배당금 내역이 없습니다.</Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* 상세 보유 현황 섹션 */}
        <Row gutter={[24, 0]}>
          <Col xs={24} className="mb-24">
            <Card bordered={false} className="criclebox">
              <Tabs
                defaultActiveKey="1"
                items={[
                  {
                    key: '1',
                    label: (
                      <span>
                        <BankOutlined />
                        계좌별 보유 현황
                      </span>
                    ),
                    children: <AccountHoldingsCard hideAmounts={hideAmounts} />,
                  },
                  {
                    key: '2',
                    label: (
                      <span>
                        <StockOutlined />
                        전체 보유 종목
                      </span>
                    ),
                    children: (
                      <div className="ant-list-box table-responsive">
                        <Table
                          columns={holdingsColumns}
                          dataSource={holdingsWithPrices}
                          pagination={{
                            pageSize: 15,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `총 ${total}개 종목`,
                          }}
                          size="small"
                          rowKey="stock_symbol"
                          locale={{ emptyText: '보유 중인 주식이 없습니다.' }}
                        />
                      </div>
                    ),
                  },
                  {
                    key: '3',
                    label: (
                      <span>
                        <PieChartOutlined />
                        보유 종목 히트맵
                      </span>
                    ),
                    children: <HoldingsHeatmap holdings={holdingsWithPrices} />,
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Home;
